import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SideMenuTypes } from '../automation-editor.service';
import { Branch } from '../component-loader/branch-options';
import {
  ADD_STEP_PREFIX,
  END_STEP_ID,
  EXPANDED_GROUP_END_PREFIX,
  EXPANDED_GROUP_START_PREFIX,
  GROUP_STEP_PREFIX,
  HIDDEN_STEP_PREFIX,
  PASSTHROUGH_STEP_PREFIX,
  SNIPPET_PREFIX,
} from '../automation-graph';
import { TRIGGER_STEP_ID } from '../../common/constants';
import { RuleInterface } from '@vendasta/automata/lib/_internal/interfaces/choice.interface';
import { JumpTaskDefinitionId } from '../component-loader/constants';
import { AutomationViewService } from '../../automation/automation-view.service';
import { WorkflowStepInterface } from '@vendasta/automata';
import { CurrentAutomationService } from '@galaxy/automata/shared';

const heightOfAddStepPx = 26;
const heightOfGroupPx = 46;
const heightOfActionPx = 171;
const heightOfJumpActionPx = 207;
const heightOfHeaderAndIconPx = 97;

@Injectable({ providedIn: 'root' })
export class WorkflowViewerV2Service {
  constructor(
    private readonly automationViewService: AutomationViewService,
    private readonly currentAutomationService: CurrentAutomationService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  centerMyselfToStep(stepId: string, isSnippet: boolean): void {
    const graphWrapper = this.document.getElementById(this.getGraphViewerWrapperId(isSnippet));
    const stepToCenterTo = this.document.getElementById(stepId);
    const backgroundPatternCircles = this.document.getElementById('pattern-circles');
    if (!graphWrapper || !stepToCenterTo) {
      console.error('centerMySelfToStep: could not find required elements');
      return;
    }

    const stepCenter = stepToCenterTo.offsetLeft + stepToCenterTo.offsetWidth / 2;
    const xPos = graphWrapper.clientWidth / 2 - stepCenter;
    const yPos = -stepToCenterTo.offsetTop + 25; // 25 is the "padding" above the action

    graphWrapper.style.transform = `translate(${xPos}px, ${yPos}px)`;
    backgroundPatternCircles?.setAttribute('x', `${xPos}px`);
    backgroundPatternCircles?.setAttribute('y', `${yPos}px`);
  }

  isMouseInsideScrollingActionContent(target: HTMLElement): boolean {
    const restrictedHeightElement = target.closest('.new-restricted-content-height');
    if (restrictedHeightElement) {
      // element is scrolling if it's height is less than it's scrollHeight
      return restrictedHeightElement.scrollHeight > restrictedHeightElement.clientHeight;
    }
    return false;
  }

  public heightOfNode(nodeId: string, stepMap: Map<string, WorkflowStepInterface>): number {
    if (
      nodeId.startsWith(ADD_STEP_PREFIX) ||
      nodeId.startsWith(HIDDEN_STEP_PREFIX) ||
      nodeId.startsWith(PASSTHROUGH_STEP_PREFIX) ||
      nodeId.startsWith(SNIPPET_PREFIX + ADD_STEP_PREFIX)
    ) {
      return heightOfAddStepPx;
    } else if (nodeId === END_STEP_ID) {
      return 40;
    }

    if (nodeId === TRIGGER_STEP_ID) {
      const ruleCount =
        this.currentAutomationService.getCurrentAutomation()?.triggerFilters?.reduce((acc: RuleInterface[], filter) => {
          acc.push(...filter.choices.rules);
          return acc;
        }, [])?.length || 0;
      return this.heightOfTriggerNode(ruleCount);
    }

    if (stepMap.get(nodeId)?.taskDefinitionId === JumpTaskDefinitionId) {
      return heightOfJumpActionPx;
    }

    if (
      this.showGroup(stepMap.get(nodeId)?.groupId) ||
      nodeId.startsWith(GROUP_STEP_PREFIX) ||
      nodeId.startsWith(EXPANDED_GROUP_START_PREFIX)
    ) {
      return heightOfGroupPx;
    }

    if (nodeId.startsWith(EXPANDED_GROUP_END_PREFIX)) {
      return 0;
    }

    const ruleCount = stepMap.get(nodeId)?.choices?.rules?.length || 0;
    if (ruleCount <= 1) {
      return heightOfActionPx;
    }
    // 32 is content padding. 88 is the first outer rule. 104 is each consecutive outer rule. 2 is border of entire thing
    return heightOfHeaderAndIconPx + 7 + 94.5 + (ruleCount - 1) * 110.5;
  }

  private heightOfTriggerNode(outerRules: number): number {
    if (outerRules === 0) {
      return heightOfActionPx;
    }
    if (outerRules === 1) {
      return heightOfActionPx + 33;
    }
    return heightOfActionPx + 33 + 79 * (outerRules - 1);
  }

  private showGroup(groupId = '') {
    return groupId && !this.automationViewService.expandedGroups().has(groupId);
  }

  getEditorViewportId(isSnippet: boolean): string {
    return isSnippet ? 'snippet-editor-view-port' : 'editor-view-port';
  }

  getZoomWrapperId(isSnippet: boolean): string {
    return isSnippet ? 'snippet-zoom-wrapper' : 'zoom-wrapper';
  }

  getGraphViewerWrapperId(isSnippet: boolean): string {
    return isSnippet ? 'snippet-graph-viewer-wrapper' : 'graph-viewer-wrapper';
  }

  getGraphSvgContainerId(isSnippet: boolean): string {
    return isSnippet ? 'snippet-graph-svg-container' : 'graph-svg-container';
  }

  getGraphNodeViewerId(isSnippet: boolean): string {
    return isSnippet ? 'snippet-graph-node-viewer' : 'graph-node-viewer';
  }
}

export interface ChangeSideMenuContent {
  requestingId: string;
  menuType: SideMenuTypes;
  precedingSteps: string[];
  taskDefinitionId?: string;
  branch?: Branch;
  groupId?: string;
}
